<template>
    <main class="policy-page position-relative overflow-hidden">
        <img class="policy-page__corner position-absolute top-0 end-0" src="/img/svg/nav-corner-1.svg" alt="">

        <div class="container position-relative py-5">
            <header class="policy-page__header mx-auto text-center">
                <p class="policy-page__eyebrow mb-3">{{content.eyebrow}}</p>
                <h1 class="display-5 fw-bolder text-gray-900 mb-3">{{content.title}}</h1>
                <p class="policy-page__intro fs-5 text-gray-600 mx-auto mb-3">{{content.intro}}</p>
                <p class="policy-page__updated mb-0">{{content.updated}}</p>
            </header>

            <aside class="policy-page__summary mx-auto d-flex align-items-start gap-3">
                <span class="policy-page__summary-icon flex-shrink-0" aria-hidden="true">
                    <BootstrapIcon :name="document === 'privacy' ? 'shield-check' : 'file-earmark-check'" :size="21" />
                </span>
                <div>
                    <h2 class="h6 fw-bolder text-gray-900 mb-1">{{content.summary_title}}</h2>
                    <p class="text-gray-600 mb-0">{{content.summary}}</p>
                </div>
            </aside>

            <div class="policy-page__layout">
                <nav class="policy-page__toc" :aria-label="content.contents_title">
                    <p class="policy-page__toc-title mb-3">{{content.contents_title}}</p>
                    <ol class="policy-page__toc-list mb-0">
                        <li v-for="(section, index) in content.sections" :key="section.id">
                            <a :href="`#${section.id}`">
                                <span>{{String(index + 1).padStart(2, '0')}}</span>
                                {{section.title}}
                            </a>
                        </li>
                    </ol>
                </nav>

                <article class="policy-page__document">
                    <section
                    v-for="(section, index) in content.sections"
                    :id="section.id"
                    :key="section.id"
                    class="policy-section scroll-mt"
                    >
                        <header class="policy-section__header d-flex align-items-start gap-3">
                            <span class="policy-section__number flex-shrink-0">{{String(index + 1).padStart(2, '0')}}</span>
                            <h2 class="h3 fw-bolder text-gray-900 mb-0">{{section.title}}</h2>
                        </header>

                        <div class="policy-section__body">
                            <div v-for="(block, blockIndex) in section.blocks" :key="blockIndex" class="policy-section__block">
                                <p v-if="block.type === 'paragraph'">{{block.text}}</p>

                                <div v-else-if="block.type === 'callout'" class="policy-section__callout">
                                    <strong v-if="block.title" class="d-block text-gray-900 mb-1">{{block.title}}</strong>
                                    <span>{{block.text}}</span>
                                </div>

                                <ul v-else-if="block.type === 'list'">
                                    <li v-for="(item, itemIndex) in block.items" :key="itemIndex">
                                        <strong v-if="item.label">{{item.label}}</strong>
                                        <span>{{item.text}}</span>
                                    </li>
                                </ul>

                                <div v-else-if="block.type === 'table'" class="policy-section__table-wrap">
                                    <table class="policy-section__table">
                                        <thead>
                                            <tr>
                                                <th v-for="heading in block.headings" :key="heading" scope="col">{{heading}}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(row, rowIndex) in block.rows" :key="rowIndex">
                                                <td v-for="(cell, cellIndex) in row" :key="cellIndex">{{cell}}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>

                                <div v-else-if="block.type === 'links'" class="policy-section__links d-flex flex-wrap gap-2">
                                    <a
                                    v-for="link in block.items"
                                    :key="link.href"
                                    :href="link.href"
                                    :target="link.href.startsWith('http') ? '_blank' : null"
                                    :rel="link.href.startsWith('http') ? 'noopener' : null"
                                    >{{link.label}}</a>
                                </div>
                            </div>
                        </div>
                    </section>

                    <footer class="policy-page__document-footer d-flex flex-wrap align-items-center justify-content-between gap-3">
                        <span class="text-gray-600">{{content.footer_note}}</span>
                        <router-link :to="{ name: relatedRoute }" class="policy-page__related-link d-inline-flex align-items-center gap-2">
                            {{content.related_label}}
                            <BootstrapIcon name="arrow-right" :size="15" aria-hidden="true" />
                        </router-link>
                    </footer>
                </article>
            </div>
        </div>
    </main>
</template>

<script>
import BootstrapIcon from '@/common/directive/BootstrapIcon'
import legalEnglish from '@/locale/legal/en.json'
import legalRomanian from '@/locale/legal/ro.json'

export default {
    components: { BootstrapIcon },
    props: {
        document: {
            type: String,
            required: true,
            validator: value => ['privacy', 'terms'].includes(value)
        }
    },
    computed: {
        locale: function () {
            return this.$store.state.locale === 'ro' ? 'ro' : 'en'
        },
        content: function () {
            const documents = this.locale === 'ro' ? legalRomanian : legalEnglish
            return documents[this.document]
        },
        relatedRoute: function () {
            return this.document === 'privacy' ? 'terms' : 'privacy'
        }
    }
}
</script>
